import { PortalDropdown } from '@/components/ui/PortalDropdown'
import { useEffect, useRef, useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'

interface RetryDropdownMenuProps {
  nodeExecution: NodeExecution
  availableStages: Array<'tool_execution' | 'edge_selection'>
  onRetry: (nodeExecutionId: string, resumeStage: 'tool_execution' | 'edge_selection') => void
  onClose: () => void
  isOpen: boolean
  buttonRef: React.RefObject<HTMLButtonElement | null>
}

export function RetryDropdownMenu({
  nodeExecution,
  availableStages,
  onRetry,
  onClose,
  isOpen,
  buttonRef,
}: RetryDropdownMenuProps) {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [portalContainer, setPortalContainer] = useState<Element | null>(null)

  useEffect(() => {
    // Find the TraceDropdown container or fallback to a higher level container
    const traceDropdown =
      document.querySelector('[class*="TraceDropdown"]') ||
      document.querySelector('[class*="trace-dropdown"]') ||
      document.querySelector('main') ||
      document.body
    setPortalContainer(traceDropdown)
  }, [])

  const handleRetry = (resumeStage: 'tool_execution' | 'edge_selection') => {
    onRetry(nodeExecution.executionId, resumeStage)
    onClose()
  }

  if (!isOpen || !portalContainer) return null

  return (
    <PortalDropdown
      isOpen={isOpen}
      anchorRef={buttonRef}
      dropdownRef={dropdownRef}
      portalContainer={portalContainer}
      strategy="below"
      onClose={onClose}
    >
      <div className="min-w-[200px] rounded-lg border border-gray-200 bg-white shadow-lg">
        <div className="p-1">
          <button
            onClick={(e) => {
              e.stopPropagation()
              availableStages.includes('tool_execution') && handleRetry('tool_execution')
            }}
            disabled={!availableStages.includes('tool_execution')}
            className={`flex w-full items-start gap-2 rounded-md px-3 py-2 text-left text-sm font-medium tracking-tight transition-colors ${
              availableStages.includes('tool_execution')
                ? 'cursor-pointer text-gray-700 hover:bg-gray-100'
                : 'cursor-not-allowed text-gray-400 opacity-60'
            }`}
          >
            <div className="flex-1">
              <div className="font-medium">Start at beginning</div>
              <div className="mt-0.5 text-xs text-gray-500">
                Rerun the entire node including tool execution
              </div>
            </div>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              availableStages.includes('edge_selection') && handleRetry('edge_selection')
            }}
            disabled={!availableStages.includes('edge_selection')}
            className={`flex w-full items-start gap-2 rounded-md px-3 py-2 text-left text-sm font-medium tracking-tight transition-colors ${
              availableStages.includes('edge_selection')
                ? 'cursor-pointer text-gray-700 hover:bg-gray-100'
                : 'cursor-not-allowed text-gray-400 opacity-60'
            }`}
          >
            <div className="flex-1">
              <div className="font-medium">Start at end</div>
              <div className="mt-0.5 text-xs text-gray-500">
                Resume from edge selection using existing outputs
              </div>
            </div>
          </button>
        </div>
      </div>
    </PortalDropdown>
  )
}
